import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  HiOutlineCog6Tooth,
  HiOutlineUsers,
  HiOutlineLightBulb,
  HiOutlineStar,
  HiOutlineExclamationTriangle,
  HiOutlineInformationCircle,
  HiOutlineCheckCircle,
  HiOutlineChartPie,
  HiOutlineShieldExclamation,
} from "react-icons/hi2";
import { CriteriaBreakdown } from "types/assessmentTypes";
import { Company } from "types/companyTypes";
import { industryWieghts } from "variables/industryWieghts";
import { getDisplayPercent } from "./helper/reportHelper";

interface PAEISectionProps {
  criteriaBreakdown: CriteriaBreakdown[];
  company: Company;
  pdfMode?: boolean;
}

export default function PAEISection({
  criteriaBreakdown,
  company,
  pdfMode = false,
}: PAEISectionProps) {
  const { t } = useTranslation();

  const PAEI_CRITERIASINDEX = [25, 28, 26, 27];

  const PAEI_MAPPING = {
    25: {
      code: "P",
      label: t("criteria.25"),
      icon: HiOutlineCog6Tooth,
      gradient: "from-blue-500 to-blue-600",
      shadow: "shadow-blue-500/30",
      lightBg: "bg-blue-50 dark:bg-blue-900/10",
    },
    28: {
      code: "A",
      label: t("criteria.28"),
      icon: HiOutlineStar,
      gradient: "from-indigo-500 to-indigo-600",
      shadow: "shadow-indigo-500/30",
      lightBg: "bg-indigo-50 dark:bg-indigo-900/10",
    },
    26: {
      code: "E",
      label: t("criteria.26"),
      icon: HiOutlineUsers,
      gradient: "from-purple-500 to-purple-600",
      shadow: "shadow-purple-500/30",
      lightBg: "bg-purple-50 dark:bg-purple-900/10",
    },
    27: {
      code: "I",
      label: t("criteria.27"),
      icon: HiOutlineLightBulb,
      gradient: "from-amber-500 to-amber-600",
      shadow: "shadow-amber-500/30",
      lightBg: "bg-amber-50 dark:bg-amber-900/10",
    },
  };

  // warning
  const shouldShowManagementWarning = (): boolean => {
    let countBelow40 = 0;

    for (const criteria of criteriaBreakdown) {
      const objectivePercent = criteria.objectivePercent;
      if (
        objectivePercent < 40 &&
        !PAEI_CRITERIASINDEX.includes(criteria.criteriaIndex)
      ) {
        countBelow40++;
        if (countBelow40 >= 10) {
          return true;
        }
      }
    }

    return false;
  };

  const showManagementWarning = shouldShowManagementWarning();

  // Get PAEI criteria data
  const getPAEIData = () => {
    const industryKey = company?.industrySector?.key;
    let totalWeightedScore = 0;
    let allWeightsSum = 0;
    const rawWeights: number[] = [];

    criteriaBreakdown.forEach((criteria) => {
      const weight =
        industryKey &&
        industryWieghts[
          criteria.criteriaIndex as keyof typeof industryWieghts
        ]?.[industryKey];
      if (typeof weight === "number") {
        rawWeights.push(weight);
        allWeightsSum += weight;
      } else {
        rawWeights.push(0);
      }
    });

    criteriaBreakdown.forEach((criteria, index) => {
      const rawWeight = rawWeights[index] || 0;
      const normalizedWeight =
        allWeightsSum > 0 ? rawWeight / allWeightsSum : 0;

      const displayPercent = getDisplayPercent(criteria);

      const weightedCriteriaPercent = displayPercent * normalizedWeight;

      totalWeightedScore += weightedCriteriaPercent;

      return {
        ...criteria,
        normalizedWeight,
        weightedCriteriaPercent,
        displayPercent,
      };
    });

    // paei
    const paeiData = PAEI_CRITERIASINDEX.map((index) => {
      const criteria = criteriaBreakdown.find((c) => c.criteriaIndex === index);
      if (!criteria) return null;

      const paeiInfo = PAEI_MAPPING[index as keyof typeof PAEI_MAPPING];
      const likertPercent = criteria.likertPercent;

      let status: "needsImprovement" | "average" | "good";
      if (likertPercent < 40) {
        status = "needsImprovement";
      } else if (likertPercent <= 60) {
        status = "average";
      } else {
        status = "good";
      }

      const statusMap = {
        needsImprovement: {
          label: t("assessment.report.status.needsImprovement"),
          color: "text-red-600 dark:text-red-400",
          bgColor: "bg-red-50 dark:bg-red-900/20",
          icon: HiOutlineExclamationTriangle,
        },
        average: {
          label: t("assessment.report.status.average"),
          color: "text-yellow-600 dark:text-yellow-400",
          bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
          icon: HiOutlineInformationCircle,
        },
        good: {
          label: t("assessment.report.status.good"),
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-900/20",
          icon: HiOutlineCheckCircle,
        },
      };

      return {
        ...criteria,
        likertPercent,
        status: statusMap[status],
        paeiInfo,
      };
    }).filter(Boolean);

    return {
      paeiData,
      overallScore: totalWeightedScore,
    };
  };

  const { paeiData, overallScore } = getPAEIData();

  const getOverallStatus = (score: number) => {
    if (score < 40) {
      return {
        label: t("assessment.report.status.needsImprovement"),
        color: "text-red-600 dark:text-red-400",
        bgColor: "bg-red-50 dark:bg-red-900/20",
        borderColor: "border-red-200 dark:border-red-800",
        gradient: "from-red-600 to-red-700",
        shadow: "shadow-red-500/30",
        icon: HiOutlineExclamationTriangle,
      };
    } else if (score <= 60) {
      return {
        label: t("assessment.report.status.average"),
        color: "text-yellow-600 dark:text-yellow-400",
        bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
        borderColor: "border-yellow-200 dark:border-yellow-800",
        gradient: "from-yellow-500 to-yellow-600",
        shadow: "shadow-yellow-500/30",
        icon: HiOutlineInformationCircle,
      };
    } else {
      return {
        label: t("assessment.report.status.good"),
        color: "text-green-600 dark:text-green-400",
        bgColor: "bg-green-50 dark:bg-green-900/20",
        borderColor: "border-green-200 dark:border-green-800",
        gradient: "from-green-600 to-green-700",
        shadow: "shadow-green-500/30",
        icon: HiOutlineCheckCircle,
      };
    }
  };

  const overallStatus = getOverallStatus(overallScore);

  if (pdfMode) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          direction: "inherit",
        }}
      >
        {/* OHI Card */}
        <div
          style={{
            borderRadius: 16,
            border: "1px solid #E5E7EB",
            background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)",
            padding: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: `linear-gradient(135deg, ${
                  overallScore >= 60
                    ? "#22c55e, #16a34a"
                    : overallScore >= 40
                    ? "#f59e0b, #d97706"
                    : "#ef4444, #dc2626"
                })`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 20px rgba(15,27,51,0.12)",
              }}
            >
              <HiOutlineChartPie size={26} color="#fff" />
            </div>
            <div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#0F1B33",
                }}
              >
                {t("assessment.report.OHI")}
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#0F1B33",
                }}
              >
                OHI (Organization Health Index)
              </div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 6,
                  padding: "3px 10px",
                  borderRadius: 999,
                  background:
                    overallScore >= 60
                      ? "#DCFCE7"
                      : overallScore >= 40
                      ? "#FEF3C7"
                      : "#FEE2E2",
                  color:
                    overallScore >= 60
                      ? "#166534"
                      : overallScore >= 40
                      ? "#92400E"
                      : "#991B1B",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <overallStatus.icon size={12} />
                {overallStatus.label}
              </div>
            </div>
          </div>

          {/* Score circle */}
          <div
            style={{
              position: "relative",
              width: 96,
              height: 96,
            }}
          >
            <svg
              viewBox="0 0 96 96"
              style={{ transform: "rotate(-90deg)", width: 96, height: 96 }}
            >
              <circle
                cx="48"
                cy="48"
                r="40"
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="8"
              />
              <circle
                cx="48"
                cy="48"
                r="40"
                fill="none"
                stroke={
                  overallScore >= 60
                    ? "#22c55e"
                    : overallScore >= 40
                    ? "#f59e0b"
                    : "#ef4444"
                }
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray="251.2"
                strokeDashoffset={251.2 - (overallScore / 100) * 251.2}
              />
            </svg>
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#0F1B33",
                }}
              >
                {overallScore.toFixed(1)}
              </span>
              <span style={{ fontSize: 10, color: "#94A3B8" }}>%</span>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
          }}
        >
          {paeiData.map((item) => {
            if (!item) return null;
            const Icon = item.paeiInfo.icon;
            const StatusIcon = item.status.icon;
            return (
              <div
                key={item.criteriaIndex}
                style={{
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: 14,
                  border: "1px solid #E5E7EB",
                  background: "#FFFFFF",
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  boxShadow: "0 4px 14px rgba(15,27,51,0.04)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${
                      item.paeiInfo.gradient.includes("blue")
                        ? "#3b82f6"
                        : item.paeiInfo.gradient.includes("indigo")
                        ? "#6366f1"
                        : item.paeiInfo.gradient.includes("purple")
                        ? "#a855f7"
                        : "#f59e0b"
                    }, transparent)`,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: `linear-gradient(135deg, ${
                          item.paeiInfo.gradient.includes("blue")
                            ? "#3b82f6, #2563eb"
                            : item.paeiInfo.gradient.includes("indigo")
                            ? "#6366f1, #4f46e5"
                            : item.paeiInfo.gradient.includes("purple")
                            ? "#a855f7, #9333ea"
                            : "#f59e0b, #d97706"
                        })`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon size={20} color="#fff" />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#0F1B33",
                          lineHeight: 1,
                        }}
                      >
                        {item.paeiInfo.code}
                      </div>
                      <div style={{ fontSize: 10, color: "#94A3B8" }}>
                        #{item.criteriaIndex}
                      </div>
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 800,
                      color:
                        item.likertPercent >= 60
                          ? "#16a34a"
                          : item.likertPercent >= 40
                          ? "#d97706"
                          : "#dc2626",
                    }}
                  >
                    {item.likertPercent.toFixed(1)}%
                  </span>
                </div>

                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#0F1B33",
                  }}
                >
                  {item.paeiInfo.label}
                </div>

                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 10px",
                    borderRadius: 999,
                    width: "fit-content",
                    background:
                      item.likertPercent >= 60
                        ? "#DCFCE7"
                        : item.likertPercent >= 40
                        ? "#FEF3C7"
                        : "#FEE2E2",
                    color:
                      item.likertPercent >= 60
                        ? "#166534"
                        : item.likertPercent >= 40
                        ? "#92400E"
                        : "#991B1B",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  <StatusIcon size={12} />
                  {item.status.label}
                </div>
              </div>
            );
          })}
        </div>

        {showManagementWarning && (
          <div
            style={{
              borderRadius: 12,
              border: "2px solid #FCD34D",
              background: "#FFFBEB",
              padding: 14,
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <HiOutlineShieldExclamation
              size={24}
              color="#D97706"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: "#92400E",
                }}
              >
                {t("assessment.report.managementWarning.title")}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color: "#B45309",
                  lineHeight: 1.6,
                }}
              >
                {t("assessment.report.managementWarning.message")}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 overflow-hidden rounded-2xl bg-gray-100/50 shadow-lg dark:bg-navy-800"
    >
      {/* Header */}
      <div className="border-b border-gray-100 p-4 dark:border-navy-700">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 p-2.5 shadow-lg shadow-brand-500/25">
              <HiOutlineChartPie className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                {t("assessment.report.PAEI")}
              </h3>
              <p className="text-xs text-gray-700 dark:text-gray-400">
                {t("assessment.report.PAEIdesc")}
              </p>
            </div>
          </div>

          {showManagementWarning && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-4 mt-4 rounded-xl border-2 border-amber-400/50 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-900/20"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <HiOutlineShieldExclamation className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-amber-800 dark:text-amber-300">
                    {t("assessment.report.managementWarning.title")}
                  </h4>
                  <p className="mt-1 text-sm text-amber-700 dark:text-amber-300/80">
                    {t("assessment.report.managementWarning.message")}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Overall Score Card - Redesigned with Clean Style */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="group relative rounded-2xl border border-gray-200/50 bg-white/90 p-2 shadow-xl backdrop-blur-sm transition-all hover:shadow-2xl dark:border-navy-600/30 dark:bg-navy-800/90 lg:p-4"
          >
            {/* Decorative gradient orbs */}
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-gradient-to-br from-brand-400/10 to-purple-400/10 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-gradient-to-tr from-blue-400/10 to-cyan-400/10 blur-3xl" />

            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50 dark:opacity-20" />

            <div className="relative flex items-center justify-between gap-4">
              {/* Left side - Title & Icon */}
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 shadow-lg shadow-brand-500/30">
                  <HiOutlineChartPie className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-navy-700 dark:text-white">
                    {t("assessment.report.OHI")}
                  </p>
                  <p className="text-md font-medium text-gray-700 dark:text-gray-200">
                    OHI
                  </p>
                  {/* Status Badge - Dynamic */}
                  <div
                    className={`flex w-fit items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-sm ${
                      overallScore >= 60
                        ? "bg-green-50 text-green-700 dark:bg-green-500/20 dark:text-green-400"
                        : overallScore >= 40
                        ? "bg-yellow-50 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400"
                        : "bg-red-50 text-red-700 dark:bg-red-500/20 dark:text-red-400"
                    }`}
                  >
                    <overallStatus.icon className="h-3.5 w-3.5" />
                    <span className="text-xs font-semibold">
                      {overallStatus.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right side - Status & Details */}
              <div className="flex flex-col items-end gap-2">
                <div className="relative flex h-24 w-24 items-center justify-center">
                  {/* Outer glow ring */}
                  <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-brand-500/20 via-purple-500/20 to-cyan-500/20 blur-xl" />

                  {/* Background circle */}
                  <svg className="relative h-24 w-24 -rotate-90 transform">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      fill="none"
                      stroke="rgba(0,0,0,0.06)"
                      strokeWidth="6"
                      className="dark:stroke-white/10"
                    />
                    {/* Progress circle with dynamic gradient */}
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      fill="none"
                      stroke={
                        overallScore >= 60
                          ? "#22c55e"
                          : overallScore >= 40
                          ? "#f59e0b"
                          : "#ef4444"
                      }
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray="251.2"
                      strokeDashoffset={251.2 - (overallScore / 100) * 251.2}
                      className="transition-all duration-1000 ease-out"
                      style={{
                        filter: `drop-shadow(0 0 12px ${
                          overallScore >= 60
                            ? "rgba(34,197,94,0.4)"
                            : overallScore >= 40
                            ? "rgba(245,158,11,0.4)"
                            : "rgba(239,68,68,0.4)"
                        })`,
                      }}
                    />
                  </svg>

                  {/* Center content - Score */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xl font-extrabold text-navy-700 dark:text-white">
                      {overallScore.toFixed(1)}
                    </span>
                    <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
                      %
                    </span>
                  </div>

                  {/* Decorative ring dots */}
                  <div className="absolute -inset-2 rounded-full border border-dashed border-gray-200/50 dark:border-gray-700/50" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* PAEI Cards Grid */}
      <div className="grid grid-cols-1 gap-4 p-2 sm:grid-cols-2 lg:grid-cols-4 lg:p-4">
        {paeiData.map((item, index) => {
          if (!item) return null;
          const Icon = item.paeiInfo.icon;
          const statusColor = item.status.color;
          const statusBg = item.status.bgColor;
          const StatusIcon = item.status.icon;

          return (
            <motion.div
              key={item.criteriaIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className={`group relative overflow-hidden rounded-xl ${item.paeiInfo.lightBg} p-5 transition-all hover:shadow-lg`}
            >
              <div className="relative">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {/* Icon with gradient */}
                    <div
                      className={`rounded-xl bg-gradient-to-br ${item.paeiInfo.gradient} p-2.5 shadow-lg ${item.paeiInfo.shadow}`}
                    >
                      <Icon className="h-5 w-5 text-white" />
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-navy-700 dark:text-white">
                          {item.paeiInfo.code}
                        </span>
                        <span className="text-[14px] font-medium text-gray-600 dark:text-gray-500">
                          #{item.criteriaIndex}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Percentage */}
                  <span
                    className={`text-2xl font-bold transition-all group-hover:scale-110 ${statusColor}`}
                  >
                    {item.likertPercent.toFixed(1)} %
                  </span>
                </div>

                {/* Criteria name */}
                <p className="mt-2 line-clamp-2 text-sm font-medium text-navy-700 dark:text-white">
                  {item.paeiInfo.label}
                </p>

                {/* Status Badge */}
                <div className="mt-3 flex items-center gap-2">
                  <div
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1 ${statusBg}`}
                  >
                    <StatusIcon className={`h-3.5 w-3.5 ${statusColor}`} />
                    <span className={`text-xs font-medium ${statusColor}`}>
                      {item.status.label}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Legend */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex flex-wrap items-center justify-center gap-4 border-t border-gray-100 px-6 py-3 dark:border-navy-700"
      >
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
          <span className="text-xs text-gray-700 dark:text-gray-400">
            {t("assessment.report.status.needsImprovement")} 0-40%
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-yellow-500" />
          <span className="text-xs text-gray-700 dark:text-gray-400">
            {t("assessment.report.status.average")} 40-60%
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
          <span className="text-xs text-gray-700 dark:text-gray-400">
            {t("assessment.report.status.good")} 60-100%
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}
